import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import { questionAPI, tagAPI, groupAPI, categoryAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { FormattedText } from '../utils/formatText';
import { Skeleton, Modal, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

function QuestionBank() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingQuestion, setViewingQuestion] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [questionFollowUps, setQuestionFollowUps] = useState({});
  const [loadingFollowUps, setLoadingFollowUps] = useState({});
  const [expandedFollowUps, setExpandedFollowUps] = useState({});
  const [savingFollowUp, setSavingFollowUp] = useState({});
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [filterTags, setFilterTags] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterCategory, setFilterCategory] = useState([]);
  const [filterQuestionType, setFilterQuestionType] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groups, setGroups] = useState([]);
  
  // Available filters
  // eslint-disable-next-line no-unused-vars
  const [allTags, setAllTags] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  
  // Pagination
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const currentLimit = parseInt(searchParams.get('limit')) || 10;
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalQuestions: 0,
    limit: 10
  });

  // Form state
  const [formData, setFormData] = useState({
    question: '',
    options: ['', '', '', ''],
    correctAnswer: 0,
    explanation: '',
    difficulty: 'Medium',
    tags: [],
    category: [],
    questionType: 'OpenEnded',
    // Coding question specific fields
    codingLanguages: ['javascript'],
    starterCode: {
      javascript: '',
      python: '',
      java: '',
      sql: '',
      csharp: ''
    },
    expectedOutput: '',
    testCases: []
  });

  // Available tags for select
  const [availableTags, setAvailableTags] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchTags();
    fetchCategories();
    if (isAdmin) {
      fetchGroups();
    } else {
      // For students, fetch their groups
      fetchStudentGroups();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchQuestions();
    // eslint-disable-next-line
  }, [currentPage, filterTags, filterDifficulty, filterCategory, filterQuestionType, currentLimit, selectedGroup]);

  const fetchGroups = async () => {
    try {
      const response = await groupAPI.getMyGroups();
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
    }
  };

  const fetchStudentGroups = async () => {
    try {
      const response = await groupAPI.getStudentGroups();
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Failed to fetch student groups:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await tagAPI.getAllTags();
      const allTags = response.data.tags || [];
      // Transform tags to {value, label} format for react-select
      const formattedTags = allTags.map(tag => ({
        value: tag.name,
        label: tag.name
      }));
      setAvailableTags(formattedTags);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getAllCategories();
      const allCategories = response.data.categories || [];
      // Transform categories to {value: ObjectId, label: name} format for react-select
      const formattedCategories = allCategories.map(cat => ({
        value: cat._id, // Store Object ID instead of name
        label: cat.name
      }));
      setAvailableCategories(formattedCategories);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      
      // For students, backend automatically filters by group tags
      // For admins, allow manual tag filtering
      let tagsToFilter = '';
      
      if (isAdmin) {
        // If admin has selected a group, filter by that group's tags
        if (selectedGroup) {
          const group = groups.find(g => g._id === selectedGroup.value);
          if (group && group.tags && group.tags.length > 0) {
            tagsToFilter = group.tags.map(tag => tag.name).join(',');
          }
        } else {
          // Otherwise use manual tag filter
          tagsToFilter = filterTags;
        }
      }
      // For students, don't send tags parameter - backend will auto-filter by group tags
      
      const filters = {
        search: searchTerm,
        difficulty: filterDifficulty,
        category: filterCategory.map(c => c.value).join(','),
        questionType: filterQuestionType
      };
      
      // Only add tags filter for admins
      if (isAdmin && tagsToFilter) {
        filters.tags = tagsToFilter;
      }
      
      const response = await questionAPI.getQuestions(currentPage, currentLimit, filters);
      setQuestions(response.data.questions);
      setPagination(response.data.pagination);
      // Tags and categories are already fetched on mount, no need to update them here
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to fetch questions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearchParams({ page: '1' });
    fetchQuestions();
  };

  const handleCreateQuestion = async (e) => {
    e.preventDefault();
    
    if (submitting) return; // Prevent double submission
    
    try {
      setSubmitting(true);
      
      const tagsArray = formData.tags ? formData.tags.map(tag => tag.value) : [];
      const categoryArray = formData.category ? formData.category.map(cat => cat.value) : [];
      const dataToSend = {
        ...formData,
        tags: tagsArray,
        category: categoryArray
      };
      
      // Only include options and correctAnswer for non-open-ended and non-coding questions
      if (formData.questionType !== 'OpenEnded' && formData.questionType !== 'Coding') {
        dataToSend.options = formData.options.filter(opt => opt.trim() !== '');
        if (dataToSend.options.length < 2) {
          showToast('Please provide at least 2 options', 'error');
          setSubmitting(false);
          return;
        }
        dataToSend.correctAnswer = formData.correctAnswer;
      } else {
        // Remove options and correctAnswer for open-ended and coding questions
        delete dataToSend.options;
        delete dataToSend.correctAnswer;
      }

      // Handle Coding question specific fields
      if (formData.questionType === 'Coding') {
        if (formData.codingLanguages.length === 0) {
          showToast('Please select at least one programming language', 'error');
          setSubmitting(false);
          return;
        }
        dataToSend.codingLanguages = formData.codingLanguages;
        dataToSend.starterCode = formData.starterCode;
        dataToSend.expectedOutput = formData.expectedOutput;
        dataToSend.testCases = formData.testCases;
      } else {
        // Remove coding fields for non-coding questions
        delete dataToSend.codingLanguages;
        delete dataToSend.starterCode;
        delete dataToSend.expectedOutput;
        delete dataToSend.testCases;
      }

      if (editingQuestion) {
        await questionAPI.updateQuestion(editingQuestion._id, dataToSend);
        showToast('Question updated successfully', 'success');
      } else {
        await questionAPI.createQuestion(dataToSend);
        showToast('Question created successfully', 'success');
      }

      resetForm();
      fetchQuestions();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to save question', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setFormData({
      question: question.question,
      options: question.options ? [...question.options, '', '', '', ''].slice(0, 4) : ['', '', '', ''],
      correctAnswer: question.correctAnswer || 0,
      explanation: question.explanation || '',
      difficulty: question.difficulty,
      tags: question.tags ? question.tags.map(tag => ({ 
        value: typeof tag === 'object' ? tag.name : tag, 
        label: typeof tag === 'object' ? tag.name : tag 
      })) : [],
      category: question.category ? question.category.map(cat => ({ 
        value: typeof cat === 'object' ? cat._id : cat, // Use Object ID
        label: typeof cat === 'object' ? cat.name : cat 
      })) : [],
      questionType: question.questionType,
      // Coding question fields
      codingLanguages: question.codingLanguages || ['javascript'],
      starterCode: question.starterCode || {
        javascript: '',
        python: '',
        java: '',
        sql: '',
        csharp: ''
      },
      expectedOutput: question.expectedOutput || '',
      testCases: question.testCases || []
    });
    setShowCreateModal(true);
  };

  const handleDeleteQuestion = async (id) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    try {
      await questionAPI.deleteQuestion(id);
      showToast('Question deleted successfully', 'success');
      fetchQuestions();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to delete question', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedQuestions.length === 0) {
      showToast('No questions selected', 'warning');
      return;
    }

    if (!window.confirm(`Delete ${selectedQuestions.length} selected question(s)?`)) return;

    try {
      await questionAPI.bulkDelete(selectedQuestions);
      showToast('Questions deleted successfully', 'success');
      setSelectedQuestions([]);
      fetchQuestions();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to delete questions', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      question: '',
      options: ['', '', '', ''],
      correctAnswer: 0,
      explanation: '',
      difficulty: 'Medium',
      tags: [],
      category: [],
      questionType: 'OpenEnded',
      // Coding question specific fields
      codingLanguages: ['javascript'],
      starterCode: {
        javascript: '',
        python: '',
        java: '',
        sql: '',
        csharp: ''
      },
      expectedOutput: '',
      testCases: []
    });
    setEditingQuestion(null);
    setShowCreateModal(false);
  };

  const handleTagCreate = async (inputValue) => {
    try {
      const response = await tagAPI.createTag(inputValue);
      const newTag = response.data.tag;
      // Format for react-select
      const formattedTag = {
        value: newTag.name,
        label: newTag.name
      };
      setAvailableTags([...availableTags, formattedTag]);
      return formattedTag;
    } catch (error) {
      showToast('Failed to create tag', 'error');
      return null;
    }
  };

  const handleCategoryCreate = async (inputValue) => {
    try {
      const response = await categoryAPI.createCategory({ name: inputValue, parent: null });
      const newCategory = response.data.category;
      // Format for react-select
      const formattedCategory = {
        value: newCategory._id,
        label: newCategory.name
      };
      setAvailableCategories([...availableCategories, formattedCategory]);
      return formattedCategory;
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to create category', 'error');
      return null;
    }
  };

  const handleTagChange = async (newValue, actionMeta) => {
    if (actionMeta.action === 'create-option') {
      const newTag = await handleTagCreate(actionMeta.option.value);
      if (newTag) {
        setFormData({ ...formData, tags: [...formData.tags, newTag] });
      }
    } else {
      setFormData({ ...formData, tags: newValue || [] });
    }
  };

  const handleCategoryChange = async (newValue, actionMeta) => {
    if (actionMeta.action === 'create-option') {
      const newCategory = await handleCategoryCreate(actionMeta.option.value);
      if (newCategory) {
        setFormData({ ...formData, category: [...formData.category, newCategory] });
      }
    } else {
      setFormData({ ...formData, category: newValue || [] });
    }
  };

  const fetchFollowUpsForQuestion = async (questionId) => {
    if (questionFollowUps[questionId]) {
      // Already loaded, just toggle
      setExpandedFollowUps(prev => ({ ...prev, [questionId]: !prev[questionId] }));
      return;
    }

    setLoadingFollowUps(prev => ({ ...prev, [questionId]: true }));
    try {
      const response = await questionAPI.getQuestionFollowUps(questionId);
      setQuestionFollowUps(prev => ({ ...prev, [questionId]: response.data.followUps || [] }));
      setExpandedFollowUps(prev => ({ ...prev, [questionId]: true }));
    } catch (error) {
      console.error('Error fetching follow-ups:', error);
      showToast('Failed to fetch follow-up questions', 'error');
    } finally {
      setLoadingFollowUps(prev => ({ ...prev, [questionId]: false }));
    }
  };

  const handleSaveFollowUpToBank = async (followUpId, questionId) => {
    if (!window.confirm('Save this follow-up question to the question bank?')) {
      return;
    }

    setSavingFollowUp(prev => ({ ...prev, [followUpId]: true }));
    try {
      await questionAPI.saveFollowUpToBank({ followUpId });
      showToast('Follow-up question saved to question bank successfully!', 'success');
      // Refresh the follow-ups for this question
      const response = await questionAPI.getQuestionFollowUps(questionId);
      setQuestionFollowUps(prev => ({ ...prev, [questionId]: response.data.followUps || [] }));
    } catch (error) {
      console.error('Error saving follow-up:', error);
      showToast(error.response?.data?.message || 'Failed to save follow-up question', 'error');
    } finally {
      setSavingFollowUp(prev => ({ ...prev, [followUpId]: false }));
    }
  };

  const toggleQuestionSelection = (id) => {
    if (selectedQuestions.includes(id)) {
      setSelectedQuestions(selectedQuestions.filter(qId => qId !== id));
    } else {
      setSelectedQuestions([...selectedQuestions, id]);
    }
  };

  const handleViewQuestion = async (questionId) => {
    try {
      const response = await questionAPI.getQuestion(questionId);
      const questionData = response.data.question || response.data;
      setViewingQuestion(questionData);
      setShowViewModal(true);
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to fetch question details', 'error');
    }
  };

  const handlePageChange = (newPage) => {
    setSearchParams({ page: newPage.toString(), limit: currentLimit.toString() });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLimitChange = (newLimit) => {
    setSearchParams({ page: '1', limit: newLimit.toString() });
  };

  const addOption = () => {
    if (formData.options.length < 6) {
      setFormData({ ...formData, options: [...formData.options, ''] });
    }
  };

  const removeOption = (index) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        options: newOptions,
        correctAnswer: formData.correctAnswer >= newOptions.length ? 0 : formData.correctAnswer
      });
    }
  };

  const updateOption = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  if (loading && questions.length === 0) {
    return (
      <div className="container">
        <div className="card">
          {/* Header skeleton */}
          <div style={{ marginBottom: '24px' }}>
            <Skeleton variant="text" width="40%" height={32} sx={{ marginBottom: '8px' }} />
            <Skeleton variant="text" width="60%" height={20} />
          </div>

          {/* Add Question button skeleton (for admin) */}
          {isAdmin && (
            <div style={{ marginBottom: '20px' }}>
              <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: '6px' }} />
            </div>
          )}

          {/* Filters section skeleton */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '12px', 
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: '#f7fafc',
            borderRadius: '8px'
          }}>
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: '6px' }} />
            {isAdmin && <Skeleton variant="rectangular" height={40} sx={{ borderRadius: '6px' }} />}
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: '6px' }} />
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: '6px' }} />
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: '6px' }} />
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: '6px' }} />
          </div>

          {/* Questions per page selector skeleton */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
            padding: '12px 16px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Skeleton variant="text" width={140} height={24} />
              <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: '6px' }} />
            </div>
            <Skeleton variant="text" width={150} height={20} />
          </div>

          {/* Questions list skeleton */}
          <div style={{ display: 'grid', gap: '12px' }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                style={{
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '16px',
                  backgroundColor: '#fafafa'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    {isAdmin && (
                      <Skeleton variant="rectangular" width={20} height={20} sx={{ borderRadius: '4px', marginTop: '4px' }} />
                    )}
                    <div style={{ flex: 1 }}>
                      {/* Number and badges */}
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <Skeleton variant="rectangular" width={40} height={20} sx={{ borderRadius: '4px' }} />
                        <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: '4px' }} />
                        <Skeleton variant="rectangular" width={80} height={20} sx={{ borderRadius: '4px' }} />
                      </div>
                      
                      {/* Question text */}
                      <Skeleton variant="text" width="90%" height={24} sx={{ marginBottom: '8px' }} />
                      <Skeleton variant="text" width="80%" height={20} />
                    </div>
                  </div>
                  
                  {/* Action buttons */}
                  {isAdmin && (
                    <div style={{ display: 'flex', gap: '8px', marginLeft: '12px' }}>
                      <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: '6px' }} />
                      <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: '6px' }} />
                    </div>
                  )}
                </div>
                
                {/* Tags */}
                <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <Skeleton variant="rectangular" width={70} height={22} sx={{ borderRadius: '11px' }} />
                  <Skeleton variant="rectangular" width={90} height={22} sx={{ borderRadius: '11px' }} />
                  <Skeleton variant="rectangular" width={60} height={22} sx={{ borderRadius: '11px' }} />
                </div>
                
                {/* View Details button */}
                <Skeleton variant="rectangular" width={140} height={36} sx={{ borderRadius: '6px' }} />
              </div>
            ))}
          </div>

          {/* Pagination skeleton */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            marginTop: '24px',
            padding: '16px'
          }}>
            <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: '6px' }} />
            <Skeleton variant="rectangular" width={36} height={36} sx={{ borderRadius: '6px' }} />
            <Skeleton variant="rectangular" width={36} height={36} sx={{ borderRadius: '6px' }} />
            <Skeleton variant="rectangular" width={36} height={36} sx={{ borderRadius: '6px' }} />
            <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: '6px' }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>Question Bank</h2>
          <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
            {isAdmin ? 'Manage standalone questions for mock interviews and quizzes' : 'Browse available questions for practice and study'}
          </p>
        </div>

        {isAdmin && (
          <div style={{ marginBottom: '20px' }}>
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              + Add Question
            </button>
          </div>
        )}

        {/* Filters */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '12px', 
          marginBottom: '20px',
          padding: '16px',
          backgroundColor: '#f7fafc',
          borderRadius: '8px'
        }}>
          <input
            type="text"
            placeholder="🔍 Search questions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px'
            }}
          />

          {isAdmin && (
            <Select
              isClearable
              value={selectedGroup}
              onChange={(selected) => {
                setSelectedGroup(selected);
                setSearchParams({ page: '1' });
              }}
              options={groups.map(group => ({ 
                value: group._id, 
                label: `${group.name} (${group.tags?.length || 0} tags)` 
              }))}
              placeholder="Filter by Group Tags"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '40px',
                  fontSize: '13px',
                  borderColor: '#e2e8f0',
                  borderWidth: '1.5px'
                }),
                singleValue: (base) => ({
                  ...base,
                  color: '#667eea',
                  fontWeight: '600'
                })
              }}
            />
          )}

          <select
            value={filterDifficulty}
            onChange={(e) => setFilterDifficulty(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              backgroundColor: 'white'
            }}
          >
            <option value="">All Difficulties</option>
            <option value="Easy">Easy</option>
            <option value="Medium">Medium</option>
            <option value="Hard">Hard</option>
          </select>

          <Select
            isMulti
            value={filterCategory}
            onChange={(selected) => setFilterCategory(selected || [])}
            options={allCategories.map(cat => ({ 
              value: typeof cat === 'object' ? cat._id : cat, 
              label: typeof cat === 'object' ? cat.name : cat 
            }))}
            placeholder="All Categories"
            isClearable
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '40px',
                fontSize: '13px',
                borderColor: '#e2e8f0',
                borderWidth: '1.5px'
              }),
              menu: (base) => ({
                ...base,
                zIndex: 9999
              }),
              menuPortal: (base) => ({
                ...base,
                zIndex: 9999
              }),
              multiValue: (base) => ({
                ...base,
                backgroundColor: '#e6fffa'
              }),
              multiValueLabel: (base) => ({
                ...base,
                color: '#234e52',
                fontSize: '12px'
              })
            }}
            menuPortalTarget={document.body}
          />

          <select
            value={filterQuestionType}
            onChange={(e) => setFilterQuestionType(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              backgroundColor: 'white'
            }}
          >
            <option value="">All Types</option>
            <option value="MCQ">Multiple Choice</option>
            <option value="TrueFalse">True/False</option>
            <option value="FillInBlank">Fill in Blank</option>
            <option value="CodeSnippet">Code Snippet</option>
            <option value="OpenEnded">Open Ended</option>
          </select>

          <button
            onClick={handleSearch}
            className="btn btn-secondary"
            style={{ padding: '8px 16px', fontSize: '13px' }}
          >
            Apply Filters
          </button>
        </div>

        {/* Show filter info for students based on their groups */}
        {!isAdmin && groups.length > 0 && groups.some(g => g.tags && g.tags.length > 0) && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#e6fffa',
            borderRadius: '8px',
            marginBottom: '16px',
            border: '1px solid #81e6d9',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '13px', color: '#234e52', fontWeight: '600' }}>
              🎓 Showing questions from your group{groups.length > 1 ? 's' : ''}: {groups.map(g => g.name).join(', ')}
            </span>
            <span style={{ fontSize: '12px', color: '#2c7a7b' }}>
              ({groups.flatMap(g => g.tags || []).map(t => t.name).filter((v, i, a) => a.indexOf(v) === i).join(', ')})
            </span>
          </div>
        )}

        {selectedGroup && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#eef2ff',
            borderRadius: '8px',
            marginBottom: '16px',
            border: '1px solid #c7d2fe',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ fontSize: '13px', color: '#667eea', fontWeight: '600' }}>
              🏷️ Filtering by group: {groups.find(g => g._id === selectedGroup.value)?.name}
              {groups.find(g => g._id === selectedGroup.value)?.tags?.length > 0 && (
                <span style={{ fontWeight: 'normal', marginLeft: '8px' }}>
                  ({groups.find(g => g._id === selectedGroup.value).tags.map(t => t.name).join(', ')})
                </span>
              )}
            </span>
            <button
              onClick={() => {
                setSelectedGroup(null);
                setSearchParams({ page: '1' });
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#667eea',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600'
              }}
            >
              Clear
            </button>
          </div>
        )}

        {/* Questions per page selector */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
          padding: '12px 16px',
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <label style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#4a5568'
            }}>
              Questions per page:
            </label>
            <select
              value={currentLimit}
              onChange={(e) => handleLimitChange(parseInt(e.target.value))}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #cbd5e0',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          <span style={{
            fontSize: '13px',
            color: '#718096'
          }}>
            Total: {pagination.totalQuestions} questions
          </span>
        </div>

        {/* Bulk Actions */}
        {isAdmin && selectedQuestions.length > 0 && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#eef2ff',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#667eea' }}>
              {selectedQuestions.length} question(s) selected
            </span>
            <button
              onClick={handleBulkDelete}
              style={{
                padding: '6px 16px',
                backgroundColor: '#fc8181',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Delete Selected
            </button>
          </div>
        )}

        {/* Questions List */}
        {questions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 24px', color: '#718096' }}>
            <p style={{ fontSize: '48px', marginBottom: '12px' }}>📝</p>
            <p style={{ fontSize: '16px', marginBottom: '8px', fontWeight: '600' }}>
              No questions yet
            </p>
            <p style={{ fontSize: '14px' }}>
              Create your first question to get started
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {questions.map((q, index) => (
              <div
                key={q._id}
                style={{
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '16px',
                  backgroundColor: '#fafafa',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    {isAdmin && (
                      <input
                        type="checkbox"
                        checked={selectedQuestions.includes(q._id)}
                        onChange={() => toggleQuestionSelection(q._id)}
                        style={{ marginTop: '4px', cursor: 'pointer' }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontWeight: '600', color: '#718096' }}>#{((currentPage - 1) * 20) + index + 1}</span>
                        <span style={{
                          padding: '3px 8px',
                          backgroundColor: q.difficulty === 'Easy' ? '#c6f6d5' : q.difficulty === 'Medium' ? '#fef3c7' : '#fecaca',
                          color: q.difficulty === 'Easy' ? '#22543d' : q.difficulty === 'Medium' ? '#78350f' : '#7f1d1d',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          {q.difficulty}
                        </span>
                        <span style={{
                          padding: '3px 8px',
                          backgroundColor: '#eef2ff',
                          color: '#667eea',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          {q.questionType}
                        </span>
                        {q.category && q.category.length > 0 && q.category.map((cat, catIdx) => (
                          <span 
                            key={catIdx}
                            style={{
                              padding: '3px 8px',
                              backgroundColor: '#e6fffa',
                              color: '#234e52',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}
                          >
                            {typeof cat === 'object' ? cat.name : cat}
                          </span>
                        ))}
                      </div>
                      <p 
                        onClick={() => handleViewQuestion(q._id)}
                        style={{ 
                          margin: '0 0 8px 0', 
                          fontSize: '14px', 
                          color: '#2d3748', 
                          lineHeight: '1.6',
                          cursor: 'pointer',
                          transition: 'color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.color = '#667eea'}
                        onMouseLeave={(e) => e.target.style.color = '#2d3748'}
                      >
                        <FormattedText text={q.question} />
                      </p>
                      {q.tags && q.tags.length > 0 && (
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                          {q.tags.map((tag, idx) => (
                            <span
                              key={idx}
                              style={{
                                padding: '2px 8px',
                                backgroundColor: '#f0f4ff',
                                color: '#667eea',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: '500',
                                border: '1px solid #dae3ff'
                              }}
                            >
                              {typeof tag === 'object' ? tag.name : tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {isAdmin && (
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={() => handleEditQuestion(q)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#f7fafc',
                          color: '#667eea',
                          border: '1.5px solid #667eea',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteQuestion(q._id)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#fff5f5',
                          color: '#c53030',
                          border: '1.5px solid #fc8181',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>

                {/* Follow-up Questions Section */}
                {isAdmin && (
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px dashed #e2e8f0' }}>
                    <button
                      onClick={() => fetchFollowUpsForQuestion(q._id)}
                      disabled={loadingFollowUps[q._id]}
                      style={{
                        padding: '6px 12px',
                        background: expandedFollowUps[q._id] ? '#9f7aea' : '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: loadingFollowUps[q._id] ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        fontWeight: 600,
                        opacity: loadingFollowUps[q._id] ? 0.6 : 1,
                        transition: 'all 0.2s'
                      }}
                    >
                      {loadingFollowUps[q._id] ? '⏳ Loading...' : 
                       expandedFollowUps[q._id] ? '🔼 Hide Follow-ups' : 
                       '🔽 View Follow-ups'}
                    </button>

                    {expandedFollowUps[q._id] && questionFollowUps[q._id] && (
                      <div style={{ marginTop: '10px' }}>
                        {questionFollowUps[q._id].length === 0 ? (
                          <p style={{ 
                            color: '#718096', 
                            fontSize: '12px', 
                            fontStyle: 'italic',
                            padding: '10px',
                            background: '#f7fafc',
                            borderRadius: '5px',
                            margin: 0
                          }}>
                            No follow-up questions have been asked for this question yet.
                          </p>
                        ) : (
                          <>
                            <div style={{ 
                              marginBottom: '10px',
                              padding: '6px 10px',
                              background: 'linear-gradient(to right, #f0f4ff, #faf5ff)',
                              borderRadius: '5px',
                              fontSize: '12px',
                              color: '#5b21b6',
                              fontWeight: 600
                            }}>
                              📚 {questionFollowUps[q._id].length} follow-up question(s) | {questionFollowUps[q._id].filter(f => f.savedToBank).length} saved to bank
                            </div>
                            {questionFollowUps[q._id].map((followUp, fuIdx) => (
                              <div 
                                key={followUp._id}
                                style={{ 
                                  background: followUp.savedToBank ? '#f0fdf4' : 'white',
                                  padding: '10px',
                                  borderRadius: '5px',
                                  marginBottom: fuIdx < questionFollowUps[q._id].length - 1 ? '8px' : '0',
                                  border: followUp.savedToBank ? '2px solid #86efac' : '1px solid #e2e8f0'
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '13px', color: '#2d3748', marginBottom: '6px', lineHeight: 1.4 }}>
                                      {followUp.followUpText}
                                    </div>
                                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                                      <span style={{ 
                                        fontSize: '10px', 
                                        padding: '2px 6px', 
                                        borderRadius: '8px', 
                                        background: '#e0e7ff', 
                                        color: '#3730a3',
                                        fontWeight: 600
                                      }}>
                                        {followUp.difficulty}
                                      </span>
                                      <span style={{ 
                                        fontSize: '10px', 
                                        padding: '2px 6px', 
                                        borderRadius: '8px', 
                                        background: '#fef3c7', 
                                        color: '#78350f',
                                        fontWeight: 600
                                      }}>
                                        Used {followUp.usageCount}x
                                      </span>
                                      {followUp.aiModelUsed && (
                                        <span style={{ 
                                          fontSize: '10px', 
                                          padding: '2px 6px', 
                                          borderRadius: '8px', 
                                          background: '#ddd6fe', 
                                          color: '#5b21b6',
                                          fontWeight: 600
                                        }}>
                                          {followUp.aiModelUsed === 'gemini' ? '🤖 Gemini' : '🤖 Deepseek'}
                                        </span>
                                      )}
                                      {followUp.savedToBank && (
                                        <span style={{ 
                                          fontSize: '10px', 
                                          padding: '2px 6px', 
                                          borderRadius: '8px', 
                                          background: '#86efac', 
                                          color: '#166534',
                                          fontWeight: 700
                                        }}>
                                          ✓ Saved to Bank
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {!followUp.savedToBank && (
                                    <button
                                      onClick={() => handleSaveFollowUpToBank(followUp._id, q._id)}
                                      disabled={savingFollowUp[followUp._id]}
                                      style={{
                                        padding: '5px 10px',
                                        background: savingFollowUp[followUp._id] ? '#cbd5e0' : '#10b981',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: savingFollowUp[followUp._id] ? 'not-allowed' : 'pointer',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                        transition: 'all 0.2s'
                                      }}
                                    >
                                      {savingFollowUp[followUp._id] ? '⏳ Saving...' : '💾 Save to Bank'}
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div style={{ 
            marginTop: '24px', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            gap: '8px',
            paddingTop: '24px',
            borderTop: '1px solid #e2e8f0'
          }}>
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pagination.hasPrevPage}
              style={{ 
                fontSize: '12px', 
                padding: '8px 16px',
                opacity: pagination.hasPrevPage ? 1 : 0.5,
                cursor: pagination.hasPrevPage ? 'pointer' : 'not-allowed'
              }}
            >
              ← Previous
            </button>

            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {[...Array(pagination.totalPages)].map((_, index) => {
                const pageNum = index + 1;
                // Show first page, last page, current page, and pages around current
                if (
                  pageNum === 1 ||
                  pageNum === pagination.totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      style={{
                        padding: '8px 12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        border: currentPage === pageNum ? '2px solid #667eea' : '1px solid #e2e8f0',
                        backgroundColor: currentPage === pageNum ? '#eef2ff' : 'white',
                        color: currentPage === pageNum ? '#667eea' : '#4a5568',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        minWidth: '36px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (currentPage !== pageNum) {
                          e.currentTarget.style.borderColor = '#667eea';
                          e.currentTarget.style.backgroundColor = '#f7fafc';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (currentPage !== pageNum) {
                          e.currentTarget.style.borderColor = '#e2e8f0';
                          e.currentTarget.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  (pageNum === currentPage - 2 && currentPage > 3) ||
                  (pageNum === currentPage + 2 && currentPage < pagination.totalPages - 2)
                ) {
                  return <span key={pageNum} style={{ color: '#718096', padding: '0 4px' }}>...</span>;
                }
                return null;
              })}
            </div>

            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pagination.hasNextPage}
              style={{ 
                fontSize: '12px', 
                padding: '8px 16px',
                opacity: pagination.hasNextPage ? 1 : 0.5,
                cursor: pagination.hasNextPage ? 'pointer' : 'not-allowed'
              }}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        open={showCreateModal}
        onClose={resetForm}
        aria-labelledby="question-modal-title"
        aria-describedby="question-modal-description"
        disableScrollLock={true}
        BackdropProps={{
          timeout: 0
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '5%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: '95%', sm: '90%', md: '700px' },
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: '12px',
            boxShadow: 24,
            p: 3,
            overflowY: 'auto',
            outline: 'none'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 id="question-modal-title" style={{ margin: 0, color: '#667eea' }}>
              {editingQuestion ? 'Edit Question' : 'Create New Question'}
            </h3>
            <IconButton
              aria-label="close"
              onClick={resetForm}
              sx={{
                color: '#718096',
                '&:hover': {
                  backgroundColor: '#f7fafc',
                  color: '#667eea'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>

            <form onSubmit={handleCreateQuestion}>
              <div className="form-group">
                <label>Question *</label>
                <textarea
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  required
                  rows="3"
                  placeholder="Enter your question..."
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label>Question Type *</label>
                  <select
                    value={formData.questionType}
                    onChange={(e) => setFormData({ ...formData, questionType: e.target.value })}
                    required
                  >
                    <option value="MCQ">Multiple Choice</option>
                    <option value="TrueFalse">True/False</option>
                    <option value="FillInBlank">Fill in Blank</option>
                    <option value="CodeSnippet">Code Snippet</option>
                    <option value="OpenEnded">Open Ended</option>
                    <option value="Coding">Coding</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Difficulty *</label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                    required
                  >
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </select>
                </div>
              </div>

              {formData.questionType !== 'OpenEnded' && formData.questionType !== 'Coding' && (
                <div className="form-group">
                  <label>Options *</label>
                  {formData.options.map((option, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => updateOption(index, e.target.value)}
                      placeholder={`Option ${String.fromCharCode(65 + index)}`}
                      style={{ flex: 1 }}
                    />
                    <input
                      type="radio"
                      name="correctAnswer"
                      checked={formData.correctAnswer === index}
                      onChange={() => setFormData({ ...formData, correctAnswer: index })}
                      style={{ width: '20px' }}
                    />
                    {formData.options.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeOption(index)}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: '#fed7d7',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                {formData.options.length < 6 && (
                  <button
                    type="button"
                    onClick={addOption}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#e2e8f0',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      cursor: 'pointer',
                      marginTop: '8px'
                    }}
                  >
                    + Add Option
                  </button>
                )}
                </div>
              )}

              {/* Coding Question Specific Fields */}
              {formData.questionType === 'Coding' && (
                <>
                  <div className="form-group">
                    <label>Supported Languages *</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '8px' }}>
                      {['javascript', 'python', 'java', 'sql', 'csharp', 'typescript'].map(lang => (
                        <label key={lang} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.codingLanguages.includes(lang)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({ ...formData, codingLanguages: [...formData.codingLanguages, lang] });
                              } else {
                                setFormData({ ...formData, codingLanguages: formData.codingLanguages.filter(l => l !== lang) });
                              }
                            }}
                            style={{ width: '18px', height: '18px' }}
                          />
                          <span style={{ fontSize: '14px', textTransform: 'capitalize' }}>
                            {lang === 'javascript' ? 'JavaScript' :
                             lang === 'python' ? 'Python' :
                             lang === 'java' ? 'Java' :
                             lang === 'sql' ? 'SQL' :
                             lang === 'csharp' ? 'C#' :
                             lang === 'typescript' ? 'TypeScript' : lang}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Starter Code (Optional)</label>
                    <p style={{ fontSize: '13px', color: '#718096', marginBottom: '12px' }}>Provide starter code for selected languages</p>
                    {formData.codingLanguages.map(lang => (
                      <div key={lang} style={{ marginBottom: '16px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#4a5568', marginBottom: '6px', display: 'block', textTransform: 'capitalize' }}>
                          {lang === 'javascript' ? 'JavaScript' :
                           lang === 'python' ? 'Python' :
                           lang === 'java' ? 'Java' :
                           lang === 'sql' ? 'SQL' :
                           lang === 'csharp' ? 'C#' :
                           lang === 'typescript' ? 'TypeScript' : lang}
                        </label>
                        <textarea
                          value={formData.starterCode[lang] || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            starterCode: { ...formData.starterCode, [lang]: e.target.value }
                          })}
                          rows="4"
                          placeholder={`// Starter code for ${lang}...`}
                          style={{ fontFamily: 'monospace', fontSize: '13px' }}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="form-group">
                    <label>Expected Output (Optional)</label>
                    <textarea
                      value={formData.expectedOutput}
                      onChange={(e) => setFormData({ ...formData, expectedOutput: e.target.value })}
                      rows="3"
                      placeholder="Describe what the code should output or accomplish..."
                      style={{ fontFamily: 'monospace', fontSize: '13px' }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Additional Details</label>
                    <textarea
                      value={formData.explanation}
                      onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
                      rows="3"
                      placeholder="Add any constraints, hints, or additional information for the coding question..."
                    />
                  </div>
                </>
              )}

              {formData.questionType !== 'Coding' && (
              <div className="form-group">
                <label>Explanation</label>
                <textarea
                  value={formData.explanation}
                  onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
                  rows="2"
                  placeholder="Optional explanation for the correct answer..."
                />
              </div>
              )}

              <div className="form-group">
                <label>Category</label>
                <CreatableSelect
                  isMulti
                  value={formData.category}
                  onChange={handleCategoryChange}
                  options={availableCategories}
                  placeholder="Select or create categories..."
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '40px',
                      fontSize: '14px',
                      borderColor: '#e2e8f0',
                      borderWidth: '1.5px'
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#e6fffa'
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: '#234e52',
                      fontWeight: '500'
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: '#234e52',
                      ':hover': {
                        backgroundColor: '#b2f5ea',
                        color: '#1a4d4d'
                      }
                    })
                  }}
                />
              </div>

              <div className="form-group">
                <label>Tags</label>
                <CreatableSelect
                  isMulti
                  value={formData.tags}
                  onChange={handleTagChange}
                  options={availableTags}
                  placeholder="Select or create tags..."
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '40px',
                      fontSize: '14px',
                      borderColor: '#e2e8f0',
                      borderWidth: '1.5px'
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#eef2ff'
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: '#667eea',
                      fontWeight: '500'
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: '#667eea',
                      ':hover': {
                        backgroundColor: '#c7d2fe',
                        color: '#5a67d8'
                      }
                    })
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button 
                  type="submit" 
                  className="btn btn-primary" 
                  style={{ flex: 1, opacity: submitting ? 0.6 : 1 }}
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : (editingQuestion ? 'Update Question' : 'Create Question')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={resetForm}
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </form>
          </Box>
        </Modal>

      {/* View Question Modal */}
      <Modal 
        open={showViewModal && viewingQuestion !== null} 
        onClose={() => setShowViewModal(false)} 
        aria-labelledby="view-question-modal"
        disableScrollLock={true}
        BackdropProps={{ timeout: 0 }}
      >
        <Box sx={{
          position: 'absolute',
          top: '5%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: { xs: '95%', sm: '90%', md: '800px' },
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          borderRadius: '12px',
          boxShadow: 24,
          p: 3.5,
          overflowY: 'auto',
          outline: 'none'
        }}>
          {viewingQuestion && (
            <>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <span style={{
                    padding: '4px 10px',
                    backgroundColor: viewingQuestion.difficulty === 'Easy' ? '#c6f6d5' : viewingQuestion.difficulty === 'Medium' ? '#fef3c7' : '#fecaca',
                    color: viewingQuestion.difficulty === 'Easy' ? '#22543d' : viewingQuestion.difficulty === 'Medium' ? '#78350f' : '#7f1d1d',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '700'
                  }}>
                    {viewingQuestion.difficulty}
                  </span>
                  <span style={{
                    padding: '4px 10px',
                    backgroundColor: '#eef2ff',
                    color: '#667eea',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '700'
                  }}>
                    {viewingQuestion.questionType}
                  </span>
                  {viewingQuestion.category && viewingQuestion.category.length > 0 && viewingQuestion.category.map((cat, catIdx) => (
                    <span 
                      key={catIdx}
                      style={{
                        padding: '4px 10px',
                        backgroundColor: '#e6fffa',
                        color: '#234e52',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '700'
                      }}
                    >
                      {typeof cat === 'object' ? cat.name : cat}
                    </span>
                  ))}
                </div>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', color: '#2d3748', lineHeight: '1.6' }}>
                  <FormattedText text={viewingQuestion.question} />
                </h3>
                {viewingQuestion.tags && viewingQuestion.tags.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {viewingQuestion.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        style={{
                          padding: '3px 10px',
                          backgroundColor: '#f0f4ff',
                          color: '#667eea',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          border: '1px solid #dae3ff'
                        }}
                      >
                        {typeof tag === 'object' ? tag.name : tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <IconButton
                onClick={() => setShowViewModal(false)}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: 16,
                  backgroundColor: '#f7fafc',
                  '&:hover': {
                    backgroundColor: '#e2e8f0'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </div>

            {/* Options */}
            {viewingQuestion.options && viewingQuestion.options.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#4a5568', marginBottom: '12px' }}>
                  Answer Options:
                </h4>
                <div style={{ display: 'grid', gap: '10px' }}>
                  {viewingQuestion.options.map((option, optIdx) => (
                    <div
                      key={optIdx}
                      style={{
                        padding: '12px 16px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        backgroundColor: optIdx === viewingQuestion.correctAnswer ? '#c6f6d5' : '#f7fafc',
                        border: optIdx === viewingQuestion.correctAnswer ? '2px solid #48bb78' : '1.5px solid #e2e8f0',
                        color: optIdx === viewingQuestion.correctAnswer ? '#2f855a' : '#4a5568',
                        fontWeight: optIdx === viewingQuestion.correctAnswer ? '600' : '500',
                        transition: 'all 0.2s'
                      }}
                    >
                      <span style={{ fontWeight: '700', marginRight: '8px', fontSize: '15px' }}>
                        {String.fromCharCode(65 + optIdx)}.
                      </span>
                      <FormattedText text={option} />
                      {optIdx === viewingQuestion.correctAnswer && (
                        <span style={{ 
                          marginLeft: '10px', 
                          fontSize: '16px',
                          fontWeight: '700'
                        }}>✓ Correct</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Explanation */}
            {viewingQuestion.explanation && (
              <div style={{
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#fffbeb',
                borderLeft: '4px solid #f59e0b',
                borderRadius: '8px'
              }}>
                <h4 style={{ 
                  fontSize: '15px', 
                  fontWeight: '700', 
                  color: '#92400e', 
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span>📖</span> Explanation
                </h4>
                <p style={{ margin: 0, fontSize: '14px', color: '#78350f', lineHeight: '1.6' }}>
                  {viewingQuestion.explanation}
                </p>
              </div>
            )}

            {/* Points to Remember */}
            {viewingQuestion.pointsToRemember && viewingQuestion.pointsToRemember.length > 0 && (
              <div style={{
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#f0f9ff',
                borderLeft: '4px solid #3b82f6',
                borderRadius: '8px'
              }}>
                <h4 style={{ 
                  fontSize: '15px', 
                  fontWeight: '700', 
                  color: '#1e40af', 
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span>💡</span> Points to Remember
                </h4>
                <ul style={{ 
                  margin: 0, 
                  paddingLeft: '24px',
                  fontSize: '14px',
                  color: '#1e40af',
                  lineHeight: '1.8'
                }}>
                  {viewingQuestion.pointsToRemember.map((point, idx) => (
                    <li key={idx} style={{ marginBottom: '6px' }}>{point}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Interviewer Points */}
            {viewingQuestion.interviewerPoints && viewingQuestion.interviewerPoints.length > 0 && (
              <div style={{
                padding: '16px',
                backgroundColor: '#fdf4ff',
                borderLeft: '4px solid #a855f7',
                borderRadius: '8px'
              }}>
                <h4 style={{ 
                  fontSize: '15px', 
                  fontWeight: '700', 
                  color: '#7e22ce', 
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span>🎯</span> What the interviewer is looking for
                </h4>
                <ul style={{ 
                  margin: 0, 
                  paddingLeft: '24px',
                  fontSize: '14px',
                  color: '#7e22ce',
                  lineHeight: '1.8'
                }}>
                  {viewingQuestion.interviewerPoints.map((point, idx) => (
                    <li key={idx} style={{ marginBottom: '6px' }}>{point}</li>
                  ))}
                </ul>
              </div>
            )}

              {/* Footer Actions */}
              <div style={{ display: 'flex', gap: '10px', marginTop: '24px', paddingTop: '20px', borderTop: '1.5px solid #e2e8f0' }}>
                {isAdmin && (
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      handleEditQuestion(viewingQuestion);
                    }}
                    className="btn btn-primary"
                    style={{ flex: 1 }}
                  >
                    Edit Question
                  </button>
                )}
                <button
                  onClick={() => setShowViewModal(false)}
                  className="btn btn-secondary"
                  style={isAdmin ? {} : { width: '100%' }}
                >
                  Close
                </button>
              </div>
            </>
          )}
        </Box>
      </Modal>
    </div>
  );
}

export default QuestionBank;
